
import { defineComponent, ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { SttHangingController } from "@/app/ui/controllers/SttHangingController";
import {
  CLIENT_ERROR,
  INTERNET_ERROR,
  INTERNAL_SERVER_ERROR
} from "@/app/infrastructures/misc/Utils";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import {
  HangingConfirmDonePosBkdPayload,
  SubmitHangingConfirmationUpdateStatus
} from "@/data/payload/api/HangingApiRequest";
import Filter from "@/app/ui/views/dashboard/component/console/hanging/tab-hanging-incoming/components/filter.vue";
import { debounce } from "lodash";
import Actions from "@/app/ui/views/dashboard/component/console/hanging/tab-hanging-incoming/components/actions.vue";
import CardProgress from "@/app/ui/views/dashboard/component/console/hanging/tab-hanging-incoming/components/card-progress.vue";
import ConfirmAction from "@/app/ui/views/dashboard/component/console/hanging/components/ConfirmAction.vue";
import CopyTemplate from "@/app/ui/views/dashboard/component/console/hanging/components/CopyTemplate.vue";
import { AccountController } from "@/app/ui/controllers/AccountController";
import TabHangingOutgoingSummary from "./summary.vue";
import { HangingBookingPosSummary } from "@/domain/entities/HangingOutgoing";
import CardCollapsible from "@/app/ui/views/dashboard/component/console/hanging/shared/card-collapsible-hanging.vue";
import {
  HangingList,
  HangingPosBkdListRequest
} from "@/domain/entities/Hanging";
import {
  CardHangingShared,
  FollowUp,
  Group,
  GroupSummary,
  Stt
} from "@/domain/entities/CardHangingShared";

export default defineComponent({
  name: "TabLastmile",
  components: {
    CardCollapsible,
    Filter,
    Actions,
    CardProgress,
    ConfirmAction,
    CopyTemplate,
    TabHangingOutgoingSummary
  },
  setup() {
    const { t } = useI18n();
    // ✅ State
    const isLoadingFetch = ref(false);
    const errorState = ref("");
    const dataSummary = ref(new HangingBookingPosSummary());
    const hangingList = ref<CardHangingShared[]>([]);
    const isLoadingList = ref(false);

    // ✅ Computed
    const accountData = computed(() => AccountController.accountData);

    // ✅ Error handler
    const handlerError = (error: any) => {
      if (!error.response) {
        errorState.value = INTERNET_ERROR;
        return;
      }
      if (error.response.status > 500) {
        errorState.value = CLIENT_ERROR;
        return;
      }
      errorState.value = INTERNAL_SERVER_ERROR;
    };

    // ✅ Fetch functions
    const getSummary = async () => {
      try {
        isLoadingFetch.value = true;
        errorState.value = "";
        dataSummary.value = await SttHangingController.getHangingBookingPosSummary();
      } catch (error) {
        handlerError(error);
      } finally {
        isLoadingFetch.value = false;
      }
    };

    const accountTypeMsg = computed(() =>
      accountData.value.isPosAccount
        ? t("dashboard.hanging.accountTypePos")
        : t("dashboard.hanging.accountTypeKonsolidator")
    );

    const getHangingList = async (params = new HangingPosBkdListRequest()) => {
      try {
        isLoadingList.value = true;
        const res = await SttHangingController.getHangingPosBkd(params);
        hangingList.value = res.map(
          (item) =>
            new CardHangingShared({
              ...item,
              followUp: new FollowUp(item.followUp),
              groups: item.groups.map(
                (group) =>
                  new Group({
                    ...group,
                    summary: new GroupSummary(group.summary),
                    stts: group.stts.map((stt) => new Stt(stt))
                  })
              )
            })
        );
      } catch (error) {
        hangingList.value = [];
      } finally {
        isLoadingList.value = false;
      }
    };

    // ✅ Lifecycle
    onMounted(async () => {
      await getSummary();
      await getHangingList();
    });

    // ✅ Modals & states
    const messageHtml = ref("");
    const statusUpdateData = ref(
      new SubmitHangingConfirmationUpdateStatus({
        customProcessStatus: "MISSING",
        sttNo: ["88LP1758092401560", "99LP1758006313265"],
        remarks: "Hanging",
        partnerId: 11325
      })
    );

    // ✅ Template modal
    const isOpenTemplate = ref(false);
    const selectedCard = ref({
      stt: [] as string[],
      groupName: ""
    });

    const onClickTemplateMessage = (item: HangingList) => {
      selectedCard.value = {
        stt: item.groups.flatMap((group) => group.stts).map((stt) => stt.sttNo),
        groupName: item.groups.flatMap((group) => group.groupName).join(",")
      };
      isOpenTemplate.value = true;
    };

    // ✅ Filters
    const filter = ref(new HangingPosBkdListRequest());

    const isEmptyList = computed(() => {
      const isEmptyFilter = Object.values(filter.value).every(
        (value) => !value
      );

      return !hangingList.value.length && !isLoadingList.value && isEmptyFilter;
    });

    const getFilter = debounce((filterInput: any) => {
      filter.value = new HangingPosBkdListRequest({
        existenceStatus: filterInput.existenceStatus,
        days: filterInput.days,
        search: filterInput.search,
        statusFollowUp: filterInput.statusFollowUp
      });

      getHangingList(filter.value);
    }, 250);

    // ✅ Status update
    const openConfirmUpdateStatus = ref(false);
    const lastStatus = ref("");

    const allSelectedStt = computed(() =>
      hangingList.value
        .flatMap((item) => item.groups)
        .flatMap((group) => group.stts)
        .filter((stt) => stt.isChecked)
        .map((stt) => stt.sttNo)
    );

    const onConfirmUpdateStatus = (statusStr: string) => {
      lastStatus.value = statusStr;
      const statusUppercase = statusStr.toUpperCase();
      const statusUpdate =
        statusUppercase === "NOT RECEIVED" ? "NOT_RECEIVED" : statusUppercase;

      messageHtml.value = t("dashboard.hanging.confirmUpdateMessage", {
        status: statusStr
      });

      statusUpdateData.value = new SubmitHangingConfirmationUpdateStatus({
        customProcessStatus: statusUpdate,
        sttNo: allSelectedStt.value,
        remarks: "Hanging"
      });

      openConfirmUpdateStatus.value = true;
    };

    const refreshList = () => {
      getSummary();
      getHangingList(filter.value);
    };

    const onDone = async (data: HangingList) => {
      try {
        openConfirmUpdateStatus.value = false;
        MainAppController.closeErrorMessage();
        MainAppController.showLoading();

        await SttHangingController.confirmDoneHangingPosBkd(
          new HangingConfirmDonePosBkdPayload({
            activityType: data.activityType,
            followUpPartnerName:
              data.activityType === "follow_up_partner_client"
                ? data.followUp?.partnerName
                : "",
            followUpPartnerCode:
              data.activityType === "follow_up_partner_client"
                ? data.followUp?.partnerCode
                : "",
            followUpPartnerType:
              data.activityType === "follow_up_partner_client"
                ? data.followUp?.partnerType
                : "",
            username:
              data.activityType === "follow_up_username"
                ? data.followUp?.username
                : ""
          })
        );

        MainAppController.setSnackbarMessage(
          `<div class="font-bold">${t("dashboard.hanging.successUpdate")}</div>`
        );
        MainAppController.setSnackbarPositivity(true);
        MainAppController.setSnackbarTimeout(2000);
        MainAppController.setSnackbar(true);

        refreshList();
      } catch (error) {
        MainAppController.setSnackbarMessage(
          `<div class="font-bold">${t("dashboard.hanging.failUpdate")}</div>`
        );
        MainAppController.setSnackbarPositivity(false);
        MainAppController.setSnackbarTimeout(2000);
        MainAppController.setSnackbar(true);
      } finally {
        MainAppController.closeLoading();
      }
    };

    const onDownload = (listStt: []) => {
      SttHangingController.donwloadHangingPosBkd(listStt);
    };

    // ✅ Expose to template
    return {
      // state
      isLoadingFetch,
      errorState,
      dataSummary,
      hangingList,
      isLoadingList,
      messageHtml,
      statusUpdateData,
      isOpenTemplate,
      selectedCard,
      filter,
      openConfirmUpdateStatus,
      lastStatus,
      // computed
      accountData,
      isEmptyList,
      allSelectedStt,
      // methods
      getFilter,
      onClickTemplateMessage,
      onConfirmUpdateStatus,
      refreshList,
      onDownload,
      onDone,
      accountTypeMsg
    };
  }
});
